import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getGroupLink, joinGroupLink } from '../transport/api'
import { Sheet } from '../ui/Sheet'
import { groupErrorText } from './groupText'
import { GroupFace } from './GroupFace'
import './groups.css'

/**
 * Opened by a group's link (groups/groupLink.ts) — owner, 2026-10-18: 拿到該 URL 就可以去選擇是否要加入這個 Group.
 * The group's name, its size and its owner, and the choice: Join group, or Not now. Already in it: Open.
 */
export function GroupLinkSheet({
  code,
  onClose,
  onJoined,
}: {
  code: string
  onClose: () => void
  /** In (just now, or already): App opens the group in Humans. */
  onJoined: (gid: string) => void
}) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['group-link', code], queryFn: () => getGroupLink(code), retry: false })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const g = q.data
  const join = async () => {
    setErr(null)
    setBusy(true)
    try {
      const r = await joinGroupLink(code)
      void qc.invalidateQueries({ queryKey: ['groups'] })
      onJoined(r.gid)
    } catch (e) {
      setErr(groupErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet title={g?.name ?? 'Group'} onClose={onClose} className="mch-sheet grp-sheet">
      {q.isPending && <p className="gh-note">Opening the link…</p>}
      {q.isError && <p className="gh-note">This link does not open a group any more.</p>}
      {g && (
        <>
          <div className="grp-linkcard">
            <GroupFace icon={g.icon} big />
            <span className="mch-meta">
              <span className="mch-name">{g.name}</span>
              <span className="grp-sub">
                {g.members} {g.members === 1 ? 'member' : 'members'}
                {g.owner ? ` · owned by ${g.owner}` : ''}
              </span>
            </span>
          </div>
          {g.status === 'member' ? (
            <>
              <p className="gh-note">You are in this group.</p>
              <button type="button" className="sheet-btn primary" onClick={() => onJoined(g.gid)}>
                Open
              </button>
            </>
          ) : (
            <>
              <p className="gh-note">
                Join to see who is in it. Its members will see your name and picture; being in a group does not
                make you friends.
              </p>
              <span className="fr-btns grp-linkgo">
                <button type="button" className="sheet-btn primary" disabled={busy} onClick={() => void join()}>
                  Join group
                </button>
                <button type="button" className="sheet-btn" onClick={onClose}>
                  Not now
                </button>
              </span>
            </>
          )}
        </>
      )}
      {err && <p className="gh-note mch-err">{err}</p>}
    </Sheet>
  )
}
